<template>
  <div class="container-fluid px-0">
    <div class="row py-4" style="max-width: 1150px; margin: 0 auto">
      <div class="col-12" v-if="!isLogged()">
        <div class="account-guest">
          <small class="account-guest__eyebrow">Mi cuenta</small>
          <h2 class="account-guest__title">Ingresa para ver tus pedidos</h2>
          <p class="account-guest__text">
            Desde aquí podrás revisar compras anteriores y seguir tus pedidos
            más recientes.
          </p>
          <div class="account-guest__actions">
            <router-link class="btn btn-primary account-guest__button" :to="getLink('ingresar')">
              Ingresar
            </router-link>
            <router-link class="btn btn-outline-primary account-guest__button" :to="getLink('registro')">
              Crear cuenta
            </router-link>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="col-12 mb-4">
          <div class="account-hero">
            <div>
              <small class="account-hero__eyebrow">Mi cuenta</small>
              <h3 class="account-hero__title mb-1">
                {{ name ? `Hola, ${name}` : "Tus pedidos" }}
              </h3>
              <small class="account-hero__email">{{ email }}</small>
            </div>
            <div class="account-hero__stat">
              <small>Pedidos</small>
              <strong>{{ total_items }}</strong>
            </div>
          </div>
        </div>

        <div class="col-12">
          <b-overlay :show="loading">
            <div class="account-list-header">
              <h5 class="mb-0">Historial de pedidos</h5>
              <small class="text-muted">{{ total_items }} registrados</small>
            </div>

            <div v-if="tickets.length == 0" class="account-empty">
              <h4 class="mb-2">Aún no tienes pedidos</h4>
              <p class="mb-0">Cuando realices una compra aparecerá aquí.</p>
            </div>

            <div v-else class="account-ticket-list">
              <button
                type="button"
                class="account-ticket"
                v-for="ticket in tickets"
                :key="ticket._id"
                :class="{
                  'account-ticket--cancelled': ticket.status == 'Cancelado',
                  'account-ticket--accepted': ticket.status == 'Aceptado',
                }"
                @click="viewTicket(ticket)"
              >
                <div class="account-ticket__top">
                  <div>
                    <strong class="account-ticket__number">#{{ ticket.number | formatNumber }}</strong>
                    <small class="d-block text-uppercase">
                      {{ ticket.createdAt | moment("dddd, DD/MM/YY") }}
                      {{ ticket.createdAt | moment("HH:mm") }}
                    </small>
                  </div>
                  <span class="account-ticket__status">
                    {{ ticket.status }}
                  </span>
                </div>

                <div class="account-ticket__meta">
                  <span>{{ ticket.type }}</span>
                  <span>{{ ticket.payment || "Sin pago definido" }}</span>
                </div>

                <div class="account-ticket__summary">
                  <span>{{ ticket.products.length }} productos</span>
                  <strong>${{ getTotal(ticket) | formatNumber }}</strong>
                </div>

                <div class="account-ticket__actions" v-if="canReorder(ticket)">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click.stop="reorderTicket(ticket)"
                  >
                    Volver a pedir
                  </button>
                </div>

                <div class="account-ticket__rating" v-if="ticket.rating">
                  <b-form-rating
                    v-model="ticket.rating.note"
                    variant="warning"
                    readonly
                    inline
                  />
                  <small class="font-italic">
                    {{ ticket.rating.comment || "- - - -" }}
                  </small>
                </div>
              </button>
            </div>

            <div class="pt-3">
              <b-pagination-nav
                :link-gen="linkGen"
                class="float-right"
                :number-of-pages="total_pages"
                exact-active-class="text-white"
                use-router
              ></b-pagination-nav>
            </div>
          </b-overlay>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      tickets: [],
      page: 0,
      total_pages: 1,
      total_items: 0,
      loading: true,
      name: "",
      email: "",
    };
  },
  mounted() {
    this.page = this.$route.query.page || 0;
    if (this.isLogged()) this.load();
    else this.loading = false;
  },
  methods: {
    canReorder(ticket) {
      if (!ticket || !ticket.createdAt || !ticket.products || ticket.products.length === 0) {
        return false;
      }

      return this.$moment().diff(this.$moment(ticket.createdAt), "days") <= 31;
    },
    reorderTicket(ticket) {
      if (!this.canReorder(ticket)) return;

      const products = (ticket.products || []).map((product, index) => ({
        ...product,
        product: product.product || product._id || null,
        options: (product.options || []).map((option) => ({
          ...option,
          values: Array.isArray(option.values)
            ? option.values.map((value) =>
                typeof value === "string"
                  ? { name: value, value: 0 }
                  : {
                      name: value && value.name ? value.name : `${value || ""}`,
                      value: value && value.value ? Number(value.value) || 0 : 0,
                    }
              )
            : typeof option.selected === "string" && option.selected
              ? option.selected
                  .split(",")
                  .map((value) => value.trim())
                  .filter(Boolean)
                  .map((value) => ({ name: value, value: 0 }))
              : [],
        })),
        tempid: `reorder-${ticket._id}-${index}-${Date.now()}`,
      }));

      this.$store.commit("setProducts", products);
      this.$router.push(this.getLink("pedido"));
    },
    viewTicket(ticket) {
      let token = ticket.createdAt.slice(-4);
      this.$router.push(this.getLink(`pedido/${ticket._id}?s=${token}`));
    },
    linkGen(pageNum) {
      return pageNum === 1 ? "?" : `?page=${pageNum}`;
    },
    getTotal(ticket) {
      return ticket.products.reduce((t, c) => t + c.total, 0);
    },
    load() {
      let info = this.$store.state.info;
      let page = this.$route.query.page;
      let token = this.$cookies.get("token");
      if (!token) {
        this.loading = false;
        return;
      }

      this.loading = true;

      this.$http
        .get("account", {
          headers: { token },
        })
        .then(({ data }) => {
          this.name = data.data.name;
          this.email = data.data.email;
        })
        .catch(() => {
          this.$cookies.remove("token");
          this.loading = false;
        });

      this.$http
        .get(`account/ticket?bid=${info._id}&page=${page}`, {
          headers: { token },
        })
        .then((res) => {
          this.tickets = res.data.data;
          this.total_pages = res.data.pages;
          this.total_items = res.data.total_items;
        })
        .catch(() => {
          this.$bvToast.toast("No se pudo cargar tu historial", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    info() {
      return this.$store.state.info;
    },
  },
  watch: {
    info() {
      if (this.isLogged()) this.load();
    },
    $route() {
      if (this.isLogged()) this.load();
    },
  },
};
</script>

<style scoped>
.account-guest,
.account-hero,
.account-empty,
.account-ticket {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.account-guest,
.account-empty {
  padding: 1.6rem;
}

.account-guest__eyebrow,
.account-hero__eyebrow {
  display: block;
  margin-bottom: 0.45rem;
  color: #0f766e;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.account-guest__title,
.account-hero__title {
  font-weight: 800;
  color: #1e293b;
}

.account-guest__text,
.account-empty p,
.account-hero__email {
  color: #64748b;
}

.account-guest__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.1rem;
}

.account-guest__button {
  min-width: 160px;
  min-height: 46px;
  border-radius: 14px;
  font-weight: 700;
}

.account-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.3rem 1.5rem;
}

.account-hero__stat {
  min-width: 120px;
  padding: 0.85rem 1rem;
  border-radius: 18px;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
  text-align: center;
}

.account-hero__stat small {
  display: block;
  color: #1d4ed8;
}

.account-hero__stat strong {
  font-size: 1.3rem;
  color: #1e3a8a;
}

.account-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.account-ticket-list {
  display: grid;
  gap: 1rem;
  max-height: 68vh;
  overflow-y: auto;
  padding-right: 0.35rem;
}

.account-ticket {
  width: 100%;
  padding: 1.2rem;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.account-ticket:hover {
  transform: translateY(-1px);
  border-color: #93c5fd;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
}

.account-ticket--accepted {
  border-color: rgba(34, 197, 94, 0.35);
}

.account-ticket--cancelled {
  border-color: rgba(239, 68, 68, 0.35);
}

.account-ticket__top,
.account-ticket__summary,
.account-ticket__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.account-ticket__meta {
  margin-top: 0.7rem;
  color: #64748b;
  font-size: 0.92rem;
}

.account-ticket__summary {
  margin-top: 0.9rem;
  color: #334155;
}

.account-ticket__number {
  font-size: 1.05rem;
  color: #1e293b;
}

.account-ticket__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #0f172a;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.account-ticket__rating {
  margin-top: 0.95rem;
}

.account-ticket__actions {
  margin-top: 1rem;
}

.bg-dark .account-guest,
.bg-dark .account-hero,
.bg-dark .account-empty,
.bg-dark .account-ticket {
  background: rgba(15, 23, 42, 0.82);
  border-color: rgba(71, 85, 105, 0.9);
}

.bg-dark .account-guest__title,
.bg-dark .account-hero__title,
.bg-dark .account-ticket__number,
.bg-dark .account-empty h4,
.bg-dark .account-list-header h5,
.bg-dark .account-ticket__summary {
  color: #f8fafc;
}

.bg-dark .account-guest__text,
.bg-dark .account-empty p,
.bg-dark .account-hero__email,
.bg-dark .account-ticket__meta,
.bg-dark .text-muted {
  color: #cbd5e1 !important;
}

@media (max-width: 767px) {
  .account-hero,
  .account-ticket__top,
  .account-ticket__summary,
  .account-list-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
